import { IconButton } from "@dropbox/dig-components/buttons";
import { IconButtonGroup } from "@dropbox/dig-components/combinations";
import { Text } from "@dropbox/dig-components/typography";
import { Box } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import {
  ChevronDownLine,
  ChevronUpLine,
} from "@dropbox/dig-icons/dist/mjs/assets";
import {
  MessageActions,
  MessageSecondaryFeedback,
} from "@mirage/conversations";
import { answersSecondaryFeedbackOptions } from "@mirage/conversations/FeedbackUtils/FeedbackUtils";
import {
  Markdown,
  MarkdownViewerWithoutTruncate,
} from "@mirage/conversations/Markdown/Markdown";
import { FeedbackOptions } from "@mirage/shared/types";
import i18n from "@mirage/translations";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { MOTION_SETTINGS } from "../Layout/constants";
import styles from "./Answer.module.css";
import { Source } from "./Source";

import type { DashAnswersFeedbackTag } from "@mirage/analytics/events/enums/dash_answers_feedback_tag";
import type {
  QuestionAndAnswerResponse,
  QuestionAndAnswerSource,
} from "@mirage/shared/answers/multi-answer";

const TRUNCATE_LINE = 7;

type AnswerProps = {
  answer: QuestionAndAnswerResponse;
  answerId: string;
  expandedByDefault: boolean;
  onSourceClick: (
    item: QuestionAndAnswerResponse,
    result: QuestionAndAnswerSource,
  ) => void;
  onShowFeedbackSnackbar: () => void;
  onCopyMessage: (item: QuestionAndAnswerResponse) => void;
  onClickFeedback: (
    item: QuestionAndAnswerResponse,
    feedback: FeedbackOptions,
  ) => void;
  onClickSecondaryFeedback: (
    item: QuestionAndAnswerResponse,
    feedback: DashAnswersFeedbackTag,
  ) => void;
  onShownSource: (
    item: QuestionAndAnswerResponse,
    result: QuestionAndAnswerSource,
  ) => void;
  onExpand: () => void;
  onCollapse: () => void;
  annotationMode?: boolean;
  onClickMore?: () => void;
  isStreamingEnabled?: boolean;
};

export const Answer: React.FC<AnswerProps> = ({
  answer,
  expandedByDefault,
  onSourceClick,
  onCopyMessage,
  onClickFeedback,
  onClickSecondaryFeedback,
  onShowFeedbackSnackbar,
  onShownSource,
  onExpand,
  onCollapse,
  annotationMode = false,
  onClickMore,
  isStreamingEnabled,
}) => {
  const [isExpanded, setIsExpanded] = useState(false); // This needs to default to false in order to prevent a "flicker" of expansion before collapsing. Some of the impression logging depends on this.
  const [isSecondaryModalOpen, setIsSecondaryModalOpen] = useState(false);

  const handleToggleExpandCollapse = () => {
    if (isExpanded) {
      onCollapse();
    } else {
      onExpand();
    }
    setIsExpanded((old) => !old);
  };

  const handleCloseSecondaryModal = () => {
    setIsSecondaryModalOpen(false);

    onShowFeedbackSnackbar();
  };

  const handleClickFeedback = (
    item: QuestionAndAnswerResponse,
    feedback: FeedbackOptions,
  ) => {
    onClickFeedback(item, feedback);
    if (annotationMode) {
      return;
    }

    if (feedback === FeedbackOptions.Negative) {
      setIsSecondaryModalOpen(true);
    } else {
      onShowFeedbackSnackbar();
    }
  };

  const handleClickSecondaryFeedback = (
    item: QuestionAndAnswerResponse,
    feedback: DashAnswersFeedbackTag,
  ) => {
    onClickSecondaryFeedback(item, feedback);
    setIsSecondaryModalOpen(false);
    onShowFeedbackSnackbar();
  };

  useEffect(() => {
    setIsExpanded(expandedByDefault);
  }, [expandedByDefault]);

  return (
    <div>
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <Text
          isBold
          variant="label"
          size="large"
          className={styles.question}
          onClick={handleToggleExpandCollapse}
        >
          {answer.question}
        </Text>
        <IconButtonGroup size="small">
          {({ getButtonProps }) => (
            <IconButton
              {...getButtonProps()}
              aria-label={isExpanded ? i18n.t("collapse") : i18n.t("expand")}
              variant="borderless"
              onClick={handleToggleExpandCollapse}
            >
              <UIIcon src={isExpanded ? ChevronUpLine : ChevronDownLine} />
            </IconButton>
          )}
        </IconButtonGroup>
      </Box>
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div {...(isStreamingEnabled ? {} : MOTION_SETTINGS)}>
            {isStreamingEnabled ? (
              <MarkdownViewerWithoutTruncate body={answer.answer} />
            ) : (
              <Markdown
                body={answer.answer}
                truncateLine={expandedByDefault ? TRUNCATE_LINE : 0}
                onToggleExpand={() => {
                  onClickMore?.();
                }}
              />
            )}
            <div className={styles.sourcesContainer}>
              {answer.sources.map((source) => (
                <Source
                  key={source.id3p}
                  source={source}
                  answer={answer}
                  onClick={onSourceClick}
                  onShown={onShownSource}
                />
              ))}
            </div>

            <Box display="flex" paddingTop="20">
              <MessageSecondaryFeedback
                isOpen={isSecondaryModalOpen}
                onClose={handleCloseSecondaryModal}
                feedbackOptions={answersSecondaryFeedbackOptions}
                onClickFeedback={(feedback) => {
                  handleClickSecondaryFeedback(answer, feedback);
                }}
                actionSurfaceComponent="inline_answer"
              />
              <MessageActions
                onClickFeedback={(feedback) => {
                  handleClickFeedback(answer, feedback);
                }}
                onCopyMessage={() => {
                  onCopyMessage(answer);
                }}
                actionSurfaceComponent="inline_answer"
              />
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
